// src/payment/adapters/QRAdapter.js
import axios from 'axios';
import API from '../../api/config/api.config';
import ENV from '../../api/config/env.config';

export class QRAdapter {
  constructor(slug, resumen, onStatusUpdate) {
    this.slug = slug;
    this.resumen = resumen;
    this.onStatusUpdate = onStatusUpdate;
    this.pollingInterval = null;
    this.attemptCount = 0;
    this.MAX_ATTEMPTS = 240; // 20 minutos a 5s
    this.isChecking = false;
    this.abortController = null;
  }

  // ==========================================
  // GENERAR QR
  // ==========================================
  async generate() {
    try {
      const uuid_cliente = this._getOrCreateUUID();
      const glosa = this._normalizarGlosa(this._generarDescripcion(), 40);
      

      // Payload simplificado: el frontend envía los datos de inscripción tal cual
      // y el backend resuelve los precios maestros desde la Base de Datos.
      const payload = {
        uuid_cliente,
        currency: 'BOB',
        gloss: glosa,
        amount: parseFloat(this.resumen.monto) || 1,
        singleUse: true,
        description: glosa,
        expirationDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        additionalData: glosa,
        slug: this.slug,
        datos_inscripcion_guardado: {
          ...this.resumen
        },
        competencia_slug: this.slug,
      };

      

      const response = await axios.post(
        `${ENV.API.BASE_URL}${API.QR.GENERATE}`,
        payload,
        { headers: this._getHeaders() }
      );

      let qrData = response.data?.qrData || response.data?.data || response.data;


      if (!qrData?.qr || !qrData?.qrId) {
        throw new Error('Respuesta de QR inválida');
      }

      return {
        qr: `data:image/png;base64,${qrData.qr}`,
        qrId: qrData.qrId,
        expirationDate: qrData.expirationDate,
        createdAt: qrData.createdAt,
        token: qrData.token,
      };
    } catch (error) {
      console.error('[QRAdapter] Error generando QR:', error);
      throw this._handleError(error);
    }
  }

  // ==========================================
  // VERIFICAR ESTADO (POLLING)
  // ==========================================
  startPolling(qrId, onSuccess, onError) {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.attemptCount = 0;

    this.pollingInterval = setInterval(async () => {
      if (this.isChecking) return;
      this.isChecking = true;
      this.attemptCount++;

      try {
        const status = await this._checkStatus(qrId);
        

        if (status === 'PAGADO' || status === 'COMPLETED' || status === 2) {
          this.stopPolling();
          onSuccess();
          return;
        }

        if (status === 'EXPIRADO' || status === 'EXPIRED' || status === 3) {
          this.stopPolling();
          onError(new Error('QR_EXPIRED'));
          return;
        }

        if (status === 'RECHAZADO' || status === 'REJECTED' || status === 4) {
          this.stopPolling();
          onError(new Error('QR_REJECTED'));
          return;
        }

        if (this.attemptCount >= this.MAX_ATTEMPTS) {
          this.stopPolling();
          onError(new Error('QR_TIMEOUT'));
          return;
        }

        if (this.onStatusUpdate) {
          const progress = Math.min((this.attemptCount / this.MAX_ATTEMPTS) * 100, 95);
          this.onStatusUpdate(progress);
        }
      } catch (error) {
        console.error('[QRAdapter] Error en polling:', error);
      } finally {
        this.isChecking = false;
      }
    }, 5000);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isChecking = false;
    this.attemptCount = 0;
  }

  // ==========================================
  // MÉTODOS PRIVADOS
  // ==========================================
  async _checkStatus(qrId) {
    const response = await axios.post(
      `${ENV.API.BASE_URL}${API.QR.STATUS}`,
      {
        qrId,
        slug: this.slug,
        timestamp: Date.now()
      },
      { headers: this._getHeaders() }
    );

    const result = response.data?.result || response.data?.status || response.data;
    return result?.statusId || result?.status || result;
  }

  _getOrCreateUUID() {
    let uuid = localStorage.getItem('user_uuid');
    if (!uuid) {
      uuid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      localStorage.setItem('user_uuid', uuid);
    }
    return uuid;
  }

  _normalizarGlosa(texto, maxLength = 40) {
    if (!texto || typeof texto !== 'string') return '';
    const clean = texto.trim();
    return clean.length > maxLength ? clean.substring(0, maxLength) : clean;
  }

  _generarDescripcion() {
    const campos = this.resumen.camposActivos || [];

    const nombre = campos.find(c =>
      c.nombre?.toLowerCase().includes('nombre')
    )?.valor || '';

    const apellido = campos.find(c =>
      c.nombre?.toLowerCase().includes('apellido')
    )?.valor || '';

    const nombreCompleto = apellido ? `${nombre} ${apellido}` : nombre;
    const paquete = this.resumen.paqueteSeleccionado?.title || 'sin-paquete';

    return `${this.slug} - ${paquete}${nombreCompleto ? ` - ${nombreCompleto}` : ''}`;
  }

  _getHeaders() {
    const token = localStorage.getItem('token');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };
  }

  _handleError(error) {
    const errorMap = {
      401: 'UNAUTHORIZED',
      404: 'NOT_FOUND',
      500: 'SERVER_ERROR',
      503: 'SERVICE_UNAVAILABLE',
    };

    if (error.response) {
      const code = errorMap[error.response.status] || 'UNKNOWN_ERROR';
      return new Error(code);
    }
    if (error.request) {
      return new Error('NETWORK_ERROR');
    }
    return error;
  }
}